import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Truck, Pill, Check, ArrowRight, ArrowLeft, ShieldCheck, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Logo from "@/components/app/Logo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAMS = {
  transport: {
    key: "transport",
    icon: Truck,
    label: "Doctobook Trans",
    tagline: "Transport sanitaire · Ambulances · VSL · Taxis conventionnés",
    accent: "emerald",
    gradient: "from-emerald-600 to-emerald-800",
    fleetLabel: "Nombre de véhicules dans votre flotte",
    fleetPlaceholder: "Ex. 5 véhicules (2 ambulances + 3 VSL)",
    benefits: [
      "Missions dispatchées en priorité dans votre zone",
      "Bon de Transport électronique signé eIDAS",
      "Tiers payant CPAM automatique",
      "0 commission les 3 premiers mois du pilote",
      "GPS temps réel + tracking patient (PWA)",
    ],
  },
  pharmacy: {
    key: "pharmacy",
    icon: Pill,
    label: "Doctobook Rx",
    tagline: "Pharmacies d'officine · Préparation & livraison sécurisée",
    accent: "coral",
    gradient: "from-[#FF7A5C] to-[#a8421f]",
    fleetLabel: "Effectif de votre officine",
    fleetPlaceholder: "Ex. 8 personnes dont 3 pharmaciens",
    benefits: [
      "Ordonnances numériques reçues directement du prescripteur",
      "Choix du mode : click&collect / livraison propre / transporteur",
      "Paiement direct au pro (lien CB, virement) OU Carte Vitale (Icanopée)",
      "Stock temps réel visible pour les prescripteurs",
      "Fiche officine dans l'annuaire national Doctobook",
    ],
  },
};

const DEPT_PACA = ["04 · Alpes-de-Haute-Provence", "05 · Hautes-Alpes", "06 · Alpes-Maritimes", "13 · Bouches-du-Rhône", "83 · Var", "84 · Vaucluse"];

export default function PartnerPreRegister() {
  const [params, setParams] = useSearchParams();
  const initialType = params.get("type") === "pharmacy" ? "pharmacy" : "transport";
  const [type, setType] = useState(initialType);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyReg, setAlreadyReg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    siret: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
    postal_code: "",
    region: "PACA",
    fleet_or_size: "",
    message: "",
    consent_rgpd: false,
    consent_communications: false,
  });

  const program = useMemo(() => PROGRAMS[type], [type]);

  const switchType = (newType) => {
    setType(newType);
    setParams({ type: newType }, { replace: true });
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const canSubmit =
    form.company_name.trim().length >= 2 &&
    form.contact_name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.city.trim().length >= 2 &&
    form.consent_rgpd;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Merci de remplir les champs obligatoires et de cocher le consentement RGPD.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads/partners`, { ...form, partner_type: type });
      setSubmitted(true);
      setAlreadyReg(data?.status === "already_registered");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const Icon = program.icon;

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center" data-testid="lead-success">
          <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${program.gradient} text-white flex items-center justify-center mb-5`}>
            <Check className="h-8 w-8" strokeWidth={3} />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">
            {alreadyReg ? "Déjà pré-inscrit" : "Pré-inscription confirmée"}
          </div>
          <h1 className="font-display text-3xl font-extrabold text-deep">Merci {form.contact_name.split(" ")[0]} !</h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            {alreadyReg
              ? "Cette adresse est déjà enregistrée pour ce programme. Notre équipe partenariats reviendra vers vous très prochainement."
              : (
                <>
                  Votre pré-inscription au programme <b>{program.label}</b> est bien enregistrée.
                  Un email de confirmation vient d'être envoyé à <b>{form.email}</b>.
                </>
              )}
          </p>
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-sm text-slate-700">
            <div className="font-semibold text-deep mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#FF7A5C]" />Prochaines étapes
            </div>
            <ol className="list-decimal list-inside space-y-1.5 text-slate-600">
              <li>Notre équipe vous contacte sous <b>5 jours ouvrés</b></li>
              <li>Entretien de qualification 15 min</li>
              <li>Lettre d'intention non engageante</li>
              <li>Onboarding pilote PACA (Q2 2026)</li>
            </ol>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-sm text-[#618DB8] font-semibold hover:underline">
            <ArrowLeft className="h-4 w-4" />Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <Link to="/partenaires" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />Retour partenaires
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 grid lg:grid-cols-[1.1fr_1fr] gap-10">
        {/* Left column — hero + program details */}
        <section>
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-4">
            <MapPin className="h-3 w-3" />Pilote PACA · Q2 2026
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-deep tracking-tight leading-[1.05]">
            Rejoignez le pilote <span className="text-[#FF7A5C]">Doctobook</span> en Provence-Alpes-Côte d'Azur
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Nous lançons un programme partenaire réservé aux <b>transporteurs sanitaires</b> et <b>pharmacies d'officine</b> agréés en région PACA.
            Pré-inscrivez-vous pour rejoindre les 20 premières structures du pilote.
          </p>

          {/* Type toggle */}
          <div className="mt-8 grid grid-cols-2 gap-3" role="tablist">
            {Object.values(PROGRAMS).map((p) => {
              const PIcon = p.icon;
              const active = type === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => switchType(p.key)}
                  className={`border rounded-xl p-4 text-left transition ${active ? "border-[#2A4365] bg-white shadow-sm" : "border-slate-200 bg-white/40 hover:bg-white"}`}
                  data-testid={`switch-${p.key}`}
                >
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${p.gradient} text-white flex items-center justify-center mb-2`}>
                    <PIcon className="h-4 w-4" />
                  </div>
                  <div className="font-bold text-deep text-sm">{p.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.tagline}</div>
                </button>
              );
            })}
          </div>

          {/* Benefits card */}
          <div className={`mt-6 bg-gradient-to-br ${program.gradient} text-white rounded-2xl p-6`}>
            <div className="flex items-center gap-2 mb-4">
              <Icon className="h-5 w-5" />
              <div className="font-display font-extrabold text-lg">{program.label} · Avantages pilote</div>
            </div>
            <ul className="space-y-2.5">
              {program.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-white/95">
                  <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex items-center gap-3 text-xs text-slate-500 bg-white rounded-xl border border-slate-200 p-4">
            <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <span>
              <b>Association DOCTOBOOK</b> · Loi 1901 · SIRET 922 912 126 00013 · Hébergement OVHcloud HDS France.
              Aucune commission n'est perçue sur les honoraires médicaux.
            </span>
          </div>
        </section>

        {/* Right column — form */}
        <aside>
          <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="lead-form">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FF7A5C] mb-1">Formulaire · Pré-inscription</div>
              <h2 className="font-display text-xl font-extrabold text-deep">Vos coordonnées</h2>
              <p className="text-xs text-slate-500 mt-1">Non engageant · Réponse sous 5 jours ouvrés</p>
            </div>

            <Field label="Raison sociale *">
              <Input required value={form.company_name} onChange={update("company_name")} placeholder={type === "transport" ? "Ex. Ambulances de la Rade" : "Ex. Pharmacie du Port"} data-testid="input-company" />
            </Field>

            <Field label="SIRET (14 chiffres)">
              <Input value={form.siret} onChange={update("siret")} placeholder="Ex. 12345678901234" inputMode="numeric" data-testid="input-siret" />
            </Field>

            <Field label="Nom du contact référent *">
              <Input required value={form.contact_name} onChange={update("contact_name")} placeholder="Prénom Nom" data-testid="input-contact" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Email pro *">
                <Input required type="email" value={form.email} onChange={update("email")} placeholder="contact@structure.fr" data-testid="input-email" />
              </Field>
              <Field label="Téléphone">
                <Input value={form.phone} onChange={update("phone")} placeholder="+33 4 94 …" inputMode="tel" data-testid="input-phone" />
              </Field>
            </div>

            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <Field label="Ville *">
                <Input required value={form.city} onChange={update("city")} placeholder="Ex. Toulon" data-testid="input-city" />
              </Field>
              <Field label="Code postal">
                <Input value={form.postal_code} onChange={update("postal_code")} placeholder="83000" inputMode="numeric" maxLength={5} data-testid="input-postal" />
              </Field>
            </div>

            <Field label="Département (PACA prioritaire)">
              <select
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800"
                data-testid="input-region"
              >
                <option value="PACA">Autre PACA</option>
                {DEPT_PACA.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value="Autre région">Autre région (hors PACA)</option>
              </select>
            </Field>

            <Field label={program.fleetLabel}>
              <Input value={form.fleet_or_size} onChange={update("fleet_or_size")} placeholder={program.fleetPlaceholder} data-testid="input-fleet" />
            </Field>

            <Field label="Votre message (optionnel)">
              <Textarea rows={3} value={form.message} onChange={update("message")} placeholder="Vos besoins, contraintes, questions…" data-testid="input-message" />
            </Field>

            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={form.consent_rgpd}
                onCheckedChange={(v) => setForm((f) => ({ ...f, consent_rgpd: !!v }))}
                data-testid="consent-rgpd"
                className="mt-0.5"
              />
              <span>
                J'accepte que Doctobook traite mes données pour me recontacter dans le cadre de ce programme partenaire.
                Conformément au RGPD, je dispose d'un droit d'accès, de rectification et de suppression via{" "}
                <a href="mailto:dpo@doctobook.com" className="text-[#618DB8] underline">dpo@doctobook.com</a>. *
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={form.consent_communications}
                onCheckedChange={(v) => setForm((f) => ({ ...f, consent_communications: !!v }))}
                data-testid="consent-comm"
                className="mt-0.5"
              />
              <span>J'accepte de recevoir des informations occasionnelles sur le lancement (optionnel).</span>
            </label>

            <Button
              type="submit"
              className="w-full h-11 btn-coral font-semibold text-base"
              disabled={!canSubmit || loading}
              data-testid="submit-lead"
            >
              {loading ? "Envoi…" : "Envoyer ma pré-inscription"}
              <ArrowRight className="h-4 w-4 ms-1.5" />
            </Button>

            <p className="text-[10px] text-slate-400 text-center">
              * champs obligatoires · Aucun paiement, aucune obligation contractuelle
            </p>
          </form>
        </aside>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
